import { sectionNames } from '@common/intl'
import { Permission } from '@gql'
import useUser from '@hooks/useUser'
import AccountTreeIcon from '@mui/icons-material/AccountTreeOutlined'
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined'
import BlindsClosedOutlinedIcon from '@mui/icons-material/BlindsClosedOutlined'
import BusinessIcon from '@mui/icons-material/BusinessOutlined'
import CallEndOutlinedIcon from '@mui/icons-material/CallEndOutlined'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import CasesOutlinedIcon from '@mui/icons-material/CasesOutlined'
import ClassIcon from '@mui/icons-material/ClassOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import LanOutlinedIcon from '@mui/icons-material/LanOutlined'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import PersonOutlined from '@mui/icons-material/PersonOutlined'
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined'
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined'
import SellOutlinedIcon from '@mui/icons-material/SellOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { appListUrl } from '@sections/apps/urls'
import { businessEntityListUrl } from '@sections/businessEntities/urls'
import { channelListUrl } from '@sections/channels/urls'
import { oversightScenarioListUrl } from '@sections/oversightScenarios/urls'
import { playbookListUrl } from '@sections/playbooks/urls'
import { safetyTermListUrl } from '@sections/safetyTerms/urls'
import {
  campaignListPath,
  campaignRoutingOverviewPath,
  caseActionResultListPath,
  caseActionTypeListPath,
  caseCloseReasonListPath,
  caseTagListPath,
  caseTypeListPath,
  financialAccountTypeListPath,
  productListPath,
  productPackageListPath,
  settingsListUrl,
  ticketActionResultListPath,
  ticketActionTypeListPath,
  ticketCloseReasonListPath,
  ticketTagListPath,
} from '@sections/settings/urls'
import { userListUrl } from '@sections/users/urls'
import { userTeamListUrl } from '@sections/userTeams/urls'
import { WindowTitle } from '@ui/WindowTitle'
import type React from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

import type { MenuSection } from './ConfigurationPage'
import ConfigurationPage from './ConfigurationPage'

export function createConfigurationMenu(intl: IntlShape): MenuSection[] {
  return [
    {
      label: intl.formatMessage({
        defaultMessage: 'User Settings',
      }),
      menuItems: [
        {
          description: intl.formatMessage({
            defaultMessage: 'Manage your users and their permissions',
            id: 'configurationMenuUsers',
          }),
          icon: <PersonOutlined fontSize="inherit" />,
          permissions: [Permission.MANAGE_USERS],
          testId: 'configuration-menu-user',
          title: intl.formatMessage(sectionNames.users),
          url: userListUrl(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Manage user teams and their business relations',
            id: 'configurationMenuUserTeams',
          }),
          icon: <PeopleOutlinedIcon fontSize="inherit" />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-user-team',
          title: intl.formatMessage(sectionNames.userTeams),
          url: userTeamListUrl(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Configure business teams',
            id: 'configurationBusinessEntities',
          }),
          icon: <BusinessIcon fontSize="inherit" />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-business-entity',
          title: intl.formatMessage(sectionNames.businessEntities),
          url: businessEntityListUrl(),
        },
      ],
    },
    {
      label: intl.formatMessage({
        defaultMessage: 'Apps',
      }),
      menuItems: [
        {
          description: intl.formatMessage({
            defaultMessage: 'Manage your apps and their permissions',
            id: 'configurationMenuApps',
          }),
          icon: <AppsOutlinedIcon fontSize="inherit" />,
          permissions: [Permission.MANAGE_APPS],
          testId: 'configuration-menu-apps',
          title: intl.formatMessage(sectionNames.apps),
          url: appListUrl(),
        },
      ],
    },
    {
      label: intl.formatMessage({
        defaultMessage: 'Messaging',
        description: 'configuration menu group',
      }),
      menuItems: [
        {
          description: intl.formatMessage({
            defaultMessage: 'Provision and configure WhatsApp messaging channels',
            id: 'configurationMenuChannels',
          }),
          icon: <ForumOutlinedIcon fontSize="inherit" />,
          permissions: [Permission.MANAGE_CHANNELS],
          testId: 'configuration-menu-channels',
          title: intl.formatMessage(sectionNames.channels),
          url: channelListUrl(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Manage bot personalities, their versions and CRM app links',
            id: 'configurationMenuPlaybooks',
          }),
          icon: <SmartToyOutlinedIcon fontSize="inherit" />,
          permissions: [Permission.MANAGE_PLAYBOOKS],
          testId: 'configuration-menu-playbooks',
          title: intl.formatMessage(sectionNames.playbooks),
          url: playbookListUrl(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Named operator-QA oversight profiles — sample rate and AI model',
            id: 'configurationMenuOversightScenarios',
          }),
          icon: <RuleOutlinedIcon fontSize="inherit" />,
          permissions: [Permission.MANAGE_PLAYBOOKS],
          testId: 'configuration-menu-oversight-scenarios',
          title: intl.formatMessage(sectionNames.oversightScenarios),
          url: oversightScenarioListUrl(),
        },
        {
          description: intl.formatMessage({
            defaultMessage:
              'The outbound word list — what warns an operator before a message goes out',
            id: 'configurationMenuSafetyTerms',
          }),
          icon: <ShieldOutlinedIcon fontSize="inherit" />,
          // The screen lives at its own top-level route, gated on this same
          // permission. Both the sidebar's Configure entry and the
          // /configuration route are gated on the UNION of the permissions
          // listed across these items, so adding this one opens the nav for a
          // safety officer who holds nothing else.
          permissions: [Permission.MANAGE_SAFETY_POLICY],
          testId: 'configuration-menu-safety-terms',
          title: intl.formatMessage(sectionNames.safetyTerms),
          url: safetyTermListUrl(),
        },
      ],
    },
    {
      label: intl.formatMessage({
        defaultMessage: 'Campaign Settings',
      }),
      menuItems: [
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage outreach campaigns',
            id: 'configurationMenuCampaigns',
          }),
          icon: <CampaignOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-campaigns',
          title: intl.formatMessage(sectionNames.campaigns),
          url: settingsListUrl(campaignListPath)(),
        },
        {
          description: intl.formatMessage({
            defaultMessage:
              'Route incoming cases onto campaigns by their source — all rules in evaluation order with capture stats',
            id: 'configurationMenuCampaignRouting',
          }),
          icon: <RuleOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-routing-overview',
          title: intl.formatMessage({
            defaultMessage: 'Campaign Routing',
            description: 'configuration menu campaign routing title',
          }),
          url: settingsListUrl(campaignRoutingOverviewPath)(),
        },
      ],
    },
    {
      label: intl.formatMessage({
        defaultMessage: 'Case Settings',
      }),
      menuItems: [
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage case types',
            id: 'configurationMenuCaseTypes',
          }),
          icon: <CasesOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-case-types',
          title: intl.formatMessage(sectionNames.caseTypes),
          url: settingsListUrl(caseTypeListPath)(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage case action types',
            id: 'configurationMenuCaseActionTypes',
          }),
          icon: <ReceiptOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-case-action-types',
          title: intl.formatMessage(sectionNames.caseActionTypes),
          url: settingsListUrl(caseActionTypeListPath)(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage case action result',
            id: 'configurationMenuCaseActionResults',
          }),
          icon: <CallEndOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-case-action-results',
          title: intl.formatMessage(sectionNames.caseActionResults),
          url: settingsListUrl(caseActionResultListPath)(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage case close reasons',
            id: 'configurationMenuCaseCloseReason',
          }),
          icon: <BlindsClosedOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-case-close-reasons',
          title: intl.formatMessage(sectionNames.caseCloseReasons),
          url: settingsListUrl(caseCloseReasonListPath)(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage case tags',
            id: 'configurationMenuCaseTags',
          }),
          icon: <SellOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-case-tags',
          title: intl.formatMessage(sectionNames.caseTags),
          url: settingsListUrl(caseTagListPath)(),
        },
      ],
    },
    {
      label: intl.formatMessage({
        defaultMessage: 'Financial account settings',
      }),
      menuItems: [
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage financial account types',
            id: 'configurationMenuFinancialAccountTypes',
          }),
          icon: <LanOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-financial-account-types',
          title: intl.formatMessage(sectionNames.financialAccountTypes),
          url: settingsListUrl(financialAccountTypeListPath)(),
        },
      ],
    },
    {
      label: intl.formatMessage({
        defaultMessage: 'Ticket settings',
      }),
      menuItems: [
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage ticket action types',
            id: 'configurationMenuTicketActionTypes',
          }),
          icon: <ReceiptOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-case-action-types',
          title: intl.formatMessage(sectionNames.ticketActionTypes),
          url: settingsListUrl(ticketActionTypeListPath)(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage ticket action results',
            id: 'configurationMenuTicketActionResults',
          }),
          icon: <CallEndOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-case-action-results',
          title: intl.formatMessage(sectionNames.ticketActionResults),
          url: settingsListUrl(ticketActionResultListPath)(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage ticket close reasons',
            id: 'configurationMenuTicketCloseReason',
          }),
          icon: <BlindsClosedOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-ticket-close-reasons',
          title: intl.formatMessage(sectionNames.ticketCloseReasons),
          url: settingsListUrl(ticketCloseReasonListPath)(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage ticket tags',
            id: 'configurationMenuTicketTags',
          }),
          icon: <SellOutlinedIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-ticket-tags',
          title: intl.formatMessage(sectionNames.ticketTags),
          url: settingsListUrl(ticketTagListPath)(),
        },
      ],
    },
    {
      label: intl.formatMessage({
        defaultMessage: 'Product settings',
      }),
      menuItems: [
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage products',
            id: 'configurationMenuProducts',
          }),
          icon: <ClassIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-products',
          title: intl.formatMessage(sectionNames.products),
          url: settingsListUrl(productListPath)(),
        },
        {
          description: intl.formatMessage({
            defaultMessage: 'Define and manage product packages',
            id: 'configurationMenuProductPackages',
          }),
          icon: <AccountTreeIcon />,
          permissions: [Permission.MANAGE_SETTINGS],
          testId: 'configuration-menu-product-packages',
          title: intl.formatMessage(sectionNames.productPackages),
          url: settingsListUrl(productPackageListPath)(),
        },
      ],
    },
  ]
}

export const configurationMenuUrl = '/configuration/'

export const ConfigurationSection: React.FC = () => {
  const user = useUser()
  const intl = useIntl()

  return (
    <>
      <WindowTitle title={intl.formatMessage(sectionNames.configuration)} />
      <ConfigurationPage menu={createConfigurationMenu(intl)} user={user.user!} />
    </>
  )
}

export default ConfigurationSection
